/******************************************************************************
 * Developers   : Sakshi Bhise
 * File         : ChatHistoryPanel.tsx
 * Date         : 09/03/2026
 * Project Name : AskHR
 * Team         : DSET
 *
 * Purpose:
 *   Adapter wrapper for the npm @teva/ui ChatHistoryPanel.
 *
 *   This wrapper keeps the existing application functionality while using the
 *   new npm ChatHistoryPanel UI:
 *     - Reads conversations from AppStateContext
 *     - Maps old Conversation objects to npm ChatHistoryConversation objects
 *     - Preserves select, rename, delete, and clear-all behavior
 *     - Ensures workspace filtering does not hide all app conversations
 ******************************************************************************/

import { useContext, useEffect, useMemo, useRef, useState } from "react";

import {
    ChatHistoryPanel as TevaChatHistoryPanel,
} from "@teva/ui";

import { AppStateContext } from "../../state/AppProvider";

import {
    ChatHistoryLoadingState,
    historyDelete,
    historyDeleteAll,
    historyList,
    historyRename,
} from "../../api";

import type { Conversation } from "../../api/models";
import { AuthContext } from "../../state/AuthContext";
import { useTranslation } from 'react-i18next';

interface ChatHistoryPanelProps {
    onClose?: () => void;
}

const parseTimestampToDate = (timestamp: string): Date => {
    // Safari (JavaScriptCore) treats ISO strings without a timezone as local time,
    // while Chrome/V8 treats them as UTC. Append Z to force UTC parsing consistently.
    let normalized = timestamp.trim().replace(" ", "T");
    if (!/Z$|[+-]\d{2}:?\d{2}$/.test(normalized)) {
        normalized += "Z";
    }
    return new Date(normalized);
};

const formatChatHistoryTimestamp = (timestamp?: string | Date | null): string => {
    if (!timestamp) return "";

    const date = timestamp instanceof Date ? timestamp : parseTimestampToDate(String(timestamp));

    if (Number.isNaN(date.getTime())) return "";

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // Guard against dates that appear to be in the future (clock skew, timezone edge cases)
    if (diffMs < 0) return "Just now";

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
        return "Just now";
    }

    if (diffMinutes < 60) {
        return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
    }

    if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
    }

    if (diffDays === 1) {
        return "Yesterday";
    }

    const isCurrentYear = date.getFullYear() === now.getFullYear();

    if (isCurrentYear) {
        return date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
        });
    }

    return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
};

const getConversationDate = (conversation: Conversation): string => {
    return conversation.date || new Date().toISOString();
};

const getConversationPreview = (conversation: Conversation): string => {
    const messages = conversation.messages ?? [];

    const firstUserMessage = messages.find((message) => message.role === "user");

    if (firstUserMessage?.content) {
        return firstUserMessage.content;
    }

    const firstMessageWithContent = messages.find((message) => Boolean(message.content));

    if (firstMessageWithContent?.content) {
        return firstMessageWithContent.content;
    }

    return conversation.title || "Previous conversation";
};

const truncatePreview = (value: string, maxLength = 90): string => {
    const normalizedValue = value.replace(/\s+/g, " ").trim();

    if (normalizedValue.length <= maxLength) {
        return normalizedValue;
    }

    return `${normalizedValue.slice(0, maxLength).trim()}...`;
};

export function ChatHistoryPanel({
    onClose,
}: ChatHistoryPanelProps) {
    const appStateContext = useContext(AppStateContext);
    const { token } = useContext(AuthContext);
    const [actionError, setActionError] = useState<string | null>(null);
    const { t } = useTranslation();
    const [isClearing, setIsClearing] = useState(false);
    const country = appStateContext?.state.loggedInUserCountry;
    const globalId = appStateContext?.state.userGId;
    const chatHistory = appStateContext?.state.chatHistory ?? [];
    const activeConversationId = appStateContext?.state.currentChat?.id ?? null;

    const loadingState = appStateContext?.state.chatHistoryLoadingState;

    const isLoading = loadingState === ChatHistoryLoadingState.Loading;
    const isError = loadingState === ChatHistoryLoadingState.Fail;

    // Pagination state — initial load fetches 10; each subsequent page fetches 25
    const [offset, setOffset] = useState(10);
    const [hasMore, setHasMore] = useState(true);
    const [isAppending, setIsAppending] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const isFetchingRef = useRef(false);
    // Prevents the initial-fill logic from re-running every time offset/hasMore changes
    const fillAttemptedRef = useRef(false);

    // Reset pagination when a fresh history load completes
    useEffect(() => {
        if (loadingState !== ChatHistoryLoadingState.Success) return;
        setOffset(10);
        setHasMore(true);
        fillAttemptedRef.current = false;
    }, [loadingState]);

    // Shared helper: finds the first scrollable child of the panel container
    const getScrollEl = () => {
        const container = containerRef.current;
        if (!container) return null;
        return Array.from(container.querySelectorAll<HTMLElement>('*')).find(el => {
            const s = window.getComputedStyle(el);
            return s.overflowY === 'auto' || s.overflowY === 'scroll';
        }) ?? null;
    };

    // Scroll listener: load the next page when the user nears the bottom
    useEffect(() => {
        const scrollEl = getScrollEl();
        if (!scrollEl) return;

        const handleScroll = async () => {
            if (isFetchingRef.current || !hasMore) return;
            const { scrollTop, scrollHeight, clientHeight } = scrollEl;
            if (scrollHeight - scrollTop - clientHeight > 150) return;

            isFetchingRef.current = true;
            setIsAppending(true);
            try {
                const page = await historyList(offset, globalId, token, 25);
                if (!page || page.length === 0) {
                    setHasMore(false);
                } else {
                    appStateContext?.dispatch({ type: 'APPEND_CHAT_HISTORY', payload: page });
                    setOffset(prev => prev + 25);
                    if (page.length < 25) setHasMore(false);
                }
            } finally {
                isFetchingRef.current = false;
                setIsAppending(false);
            }
        };

        scrollEl.addEventListener('scroll', handleScroll);
        return () => scrollEl.removeEventListener('scroll', handleScroll);
    }, [offset, hasMore, globalId, token]);

    // One-time initial fill: if the 10 loaded records don't make the list scrollable,
    // keep fetching pages of 25 until the panel overflows or there are no more records.
    useEffect(() => {
        if (loadingState !== ChatHistoryLoadingState.Success) return;
        if (fillAttemptedRef.current) return;
        fillAttemptedRef.current = true;

        const run = async () => {
            // Wait for the DOM to reflect the initial records
            await new Promise(r => setTimeout(r, 150));

            const scrollEl = getScrollEl();
            if (!scrollEl) return;

            let localOffset = 10;
            let localHasMore = true;

            while (localHasMore && scrollEl.scrollHeight <= scrollEl.clientHeight) {
                if (isFetchingRef.current) break;
                isFetchingRef.current = true;
                setIsAppending(true);
                try {
                    const page = await historyList(localOffset, globalId, token, 25);
                    if (!page || page.length === 0) {
                        setHasMore(false);
                        localHasMore = false;
                    } else {
                        appStateContext?.dispatch({ type: 'APPEND_CHAT_HISTORY', payload: page });
                        localOffset += 25;
                        setOffset(localOffset);
                        if (page.length < 25) {
                            setHasMore(false);
                            localHasMore = false;
                        }
                    }
                } catch {
                    localHasMore = false;
                } finally {
                    isFetchingRef.current = false;
                    setIsAppending(false);
                }
                // Yield so React can flush new items into the DOM before checking height again
                if (localHasMore) await new Promise(r => setTimeout(r, 100));
            }
        };

        run();
    }, [loadingState]);


    const conversations = useMemo<any[]>(() => {
        return chatHistory
            .map((conversation) => {
                const date = getConversationDate(conversation);
                const title = conversation.title || "Untitled conversation";

                return {
                    id: conversation.id,
                    title,
                    preview: truncatePreview(getConversationPreview(conversation)),
                    timestamp: formatChatHistoryTimestamp(date),
                    workspace: "Personal Workspace", // required by @teva/ui internal filter — has no effect on UI
                };
            })
            .sort((a, b) => {
                const originalA = chatHistory.find((item) => item.id === a.id);
                const originalB = chatHistory.find((item) => item.id === b.id);

                const dateA = new Date(originalA?.date ?? 0).getTime();
                const dateB = new Date(originalB?.date ?? 0).getTime();

                return dateB - dateA;
            });
    }, [chatHistory]);


    const handleConversationSelect = (conversationId: string) => {
        const selectedConversation = chatHistory.find(
            (conversation) => conversation.id === conversationId
        );

        if (!selectedConversation) {
            return;
        }

        appStateContext?.dispatch({
            type: "UPDATE_CURRENT_CHAT",
            payload: selectedConversation,
        });

        onClose?.();
    };

    const handleRename = async (conversationId: string, newTitle: string) => {
        const title = newTitle.trim();

        if (!title) {
            return;
        }

        const currentConversation = chatHistory.find(
            (conversation) => conversation.id === conversationId
        );

        if (!currentConversation || currentConversation.title === title) {
            return;
        }

        try {
            setActionError(null);

            const response = await historyRename(conversationId, title, country, globalId, token, appStateContext?.state.impUserCountry);

            if (!response.ok) {
                throw new Error("Failed to rename conversation.");
            }

            appStateContext?.dispatch({
                type: "UPDATE_CHAT_TITLE",
                payload: {
                    ...currentConversation,
                    title,
                } as Conversation,
            });
        } catch (error) {
            console.error("Error renaming chat history item:", error);
            setActionError("Could not rename this conversation.");
        }
    };

    const handleDelete = async (conversationId: string) => {
        const conversationToDelete = chatHistory.find(
            (conversation) => conversation.id === conversationId
        );

        if (!conversationToDelete) {
            return;
        }

        try {
            setActionError(null);

            const response = await historyDelete(
                conversationToDelete.id,
                conversationToDelete.title,
                country, globalId, token, appStateContext?.state.impUserCountry
            );

            if (!response.ok) {
                throw new Error("Failed to delete conversation.");
            }

            appStateContext?.dispatch({
                type: "DELETE_CHAT_ENTRY",
                payload: conversationToDelete.id,
            });
        } catch (error) {
            console.error("Error deleting chat history item:", error);
            setActionError("Could not delete this conversation.");
        }
    };

    const handleClearAll = async () => {
        try {
            setIsClearing(true);
            setActionError(null);

            const response = await historyDeleteAll(token);

            if (!response.ok) {
                throw new Error("Failed to clear chat history.");
            }

            appStateContext?.dispatch({
                type: "DELETE_CHAT_HISTORY",
            });
        } catch (error) {
            console.error("Error clearing all chat history:", error);
            setActionError("Could not clear chat history.");
        } finally {
            setIsClearing(false);
        }
    };

    return (
        <div ref={containerRef} style={{ position: 'relative', height: '100%' }}>
            <TevaChatHistoryPanel
                onClose={onClose ?? (() => { })}
                conversations={conversations}
                activeConversationId={activeConversationId}
                onConversationSelect={handleConversationSelect}
                onRename={handleRename}
                onDelete={handleDelete}
                onClearAll={handleClearAll}
                workspaceDropdownVisible={false}
                labels={{
                    title: t("chatHistoryPanel.title"),
                    deleteAllChatsLabel: t("chatHistoryPanel.contextMenu.clearAll"),
                    deleteAllChatsAriaLabel: t("chatHistoryPanel.contextMenu.clearAll"),
                    searchPlaceholder: t("common.search", { value: t("common.chats") }),
                    emptyHistoryTitle: isLoading
                        ? t("chat.loadingChatHistory")
                        : isError
                            ? t("chatHistoryPanel.error.loading")
                            : t("chatHistoryList.noChatHistory"),
                    emptyHistorySubtitle:
                        actionError ||
                        (isError
                            ? appStateContext?.state.isSqlDBAvailable?.status ||
                            t("chatHistoryPanel.error.loading")
                            : t("chatHistoryList.noChatHistorySubtitle")),
                    emptySearchTitle: t("chatHistoryList.noChatHistory"),
                    emptySearchSubtitle: t("common.tryADifferentSearchTerm"),
                    groupRecentLabel: t("chatHistoryList.recent"),
                    deleteAllDialogTitle: t("chatHistoryPanel.clearAllDialog.title"),
                    deleteAllDialogSubText: '',
                    deleteAllDialogBodyIntro: t('chatHistoryPanel.clearAllDialog.subText'),
                    deleteAllDialogItems: [],
                    deleteAllCancelLabel: t("common.cancel"),
                    deleteAllConfirmLabel: t("common.confirm")
                }}
                conversationItemLabels={{
                    renameLabel: t("common.edit"),
                    deleteLabel: t("common.delete"),
                    deleteDialogTitle: t('chatHistoryListItemCell.deleteDialog.title'),
                    deleteDialogSubText: '',
                    deleteDialogBodyIntro: t('chatHistoryListItemCell.deleteDialog.subText'),
                    deleteDialogItems: [],
                    cancelLabel: t("common.cancel"),
                    confirmDeleteLabel: t("common.confirm")
                }}


            />
            {isAppending && (
                <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 10, fontSize: 12, color: '#666', pointerEvents: 'none' }}>
                    {t("chatHistoryListItemGroups.loadingMore")}…
                </div>
            )}
        </div>
    );
}